import type { Value } from '@fctg-corporate/smartscript-core';
import type {
	OktaAuth,
} from '@okta/okta-auth-js';
import {
	useState,
	useEffect,
} from 'react';
import {
	setCookie,
	checkCookieIsSet,
	calculateMaxAgeFromExpiresAt,
} from '@fctg-corporate/smartscript-core';

export const doSetOktaAccessTokenCookie = async (
	oktaAuth: OktaAuth,
	tokenName: string,
): Promise<void> => {
	const token = await oktaAuth.tokenManager.get(tokenName);

	if (!token || !('accessToken' in token) || !token.expiresAt) {
		return;
	}

	const maxAge = calculateMaxAgeFromExpiresAt(token.expiresAt);

	setCookie({
		cookieName: tokenName,
		token: token.accessToken,
		maxAge,
	});
};

const FIVE_HUNDRED_MILLISECONDS = 500;

export const useCookieListener = (tokenName: string): boolean => {
	const [cookieIsSet, setCookieIsSet] = useState(() => checkCookieIsSet(tokenName));

	useEffect(() => {
		const nextState = !cookieIsSet;

		const intervalID = setInterval(() => {
			if (checkCookieIsSet(tokenName) === nextState) {
				setCookieIsSet(nextState);
			}
		}, FIVE_HUNDRED_MILLISECONDS);

		return () => {
			clearInterval(intervalID);
		};
	}, [cookieIsSet, tokenName]);

	return cookieIsSet;
};

export const getSmartscriptFromQuery = (): Value => {
	const params = new URLSearchParams(window.location.search);

	return params.get('smartscript') ?? undefined;
};

export const setLocalStorage = (name: string, value: Value): void => {
	if (value) {
		localStorage.setItem(name, value);
	}
};

export const checkTokenValidity = async (oktaAuth: OktaAuth, tokenName: string): Promise<boolean> => {
	const token = await oktaAuth.tokenManager.get(tokenName);
	const hasTokenExpired = !!token && oktaAuth.tokenManager.hasExpired(token);

	return !!token && !hasTokenExpired;
};
